import type { Hoja } from '@/api/laminas';
import type { Tile } from './CapaTiles';
import type { Vista } from './CapaTiles';
import { CAPAS_TODAS } from './PanelCapas';
import type { Capas } from './PanelCapas';

// F-7 de ARQ-007: el mecanismo de «Compartir», sin una línea de React. Lo que
// Procore ofrece en ese menú, medido en la segunda grabación, son tres cosas:
// Imprimir, instantánea por correo y «Remover plano del dispositivo». Las dos
// últimas no se traducen literales a un navegador y el panel lo dice:
//
//   - La instantánea existe, pero el correo no se puede mandar desde acá: un
//     `mailto:` no adjunta archivos. Se resuelve descargando el PNG o
//     copiándolo al portapapeles, que es lo que se pega en el correo.
//   - «Remover del dispositivo» es la caché offline de una app nativa. La web
//     no guarda el plano: cada tile se pide y la caché es la del navegador. No
//     hay nada que remover, y fingir un botón sería mentir.
//
// Y agrega la que SÍ es nativa de la web y la app de Procore no tiene: un
// ENLACE a esta vista exacta. Es la forma real en que esto se comparte acá.

// ── El enlace ───────────────────────────────────────────────────────────────

// El orden es parte del formato del enlace: cambiarlo invalida los que ya se
// mandaron. Si alguna vez entra una capa nueva, va AL FINAL.
const ORDEN_CAPAS: (keyof Capas)[] = [
  'marcas', 'medidas', 'fotos', 'chinches', 'vinculos', 'publicadas', 'personales',
];

export function capasAClave(c: Capas): string {
  return ORDEN_CAPAS.map(k => (c[k] ? '1' : '0')).join('');
}

export function claveACapas(s: string | null | undefined): Capas {
  if (!s || s.length !== ORDEN_CAPAS.length) return CAPAS_TODAS;
  const out = { ...CAPAS_TODAS };
  ORDEN_CAPAS.forEach((k, i) => { out[k] = s[i] === '1'; });
  return out;
}

/**
 * Una vista compartible. El encuadre viaja como el PUNTO DE LA HOJA que queda
 * en el centro (`cx`, `cy`) más el zoom, no como el `x`/`y` del visor: ese par
 * está en píxeles de PANTALLA y depende del tamaño de la ventana, así que el
 * enlace mostraría otra región en un monitor distinto al de quien lo armó.
 */
export interface VistaCompartida {
  idPlano: number;
  idHoja: number | null;
  cx: number;
  cy: number;
  z: number;
  capas: Capas;
}

export function leerVistaCompartida(busqueda: string): VistaCompartida | null {
  const p = new URLSearchParams(busqueda);
  const idPlano = Number(p.get('lamina'));
  if (!Number.isFinite(idPlano) || idPlano <= 0) return null;
  const num = (clave: string, porDefecto: number) => {
    const v = Number(p.get(clave));
    return Number.isFinite(v) ? v : porDefecto;
  };
  const idHoja = Number(p.get('hoja'));
  return {
    idPlano,
    idHoja: Number.isFinite(idHoja) && idHoja > 0 ? idHoja : null,
    cx: num('cx', 0),
    cy: num('cy', 0),
    // 0 = «no vino zoom»: el visor encaja la hoja como siempre.
    z: num('z', 0),
    capas: claveACapas(p.get('capas')),
  };
}

export function urlDeVista(v: VistaCompartida): string {
  const p = new URLSearchParams();
  p.set('lamina', String(v.idPlano));
  if (v.idHoja) p.set('hoja', String(v.idHoja));
  // Enteros para el centro y 3 decimales para el zoom: el píxel de hoja no se
  // subdivide y un enlace con catorce decimales no se puede leer ni pegar.
  p.set('cx', String(Math.round(v.cx)));
  p.set('cy', String(Math.round(v.cy)));
  p.set('z', v.z.toFixed(3));
  p.set('capas', capasAClave(v.capas));
  return `${window.location.origin}${window.location.pathname}?${p.toString()}`;
}

/** Saca los parámetros del enlace sin recargar ni tocar el router. */
export function limpiarEnlaceDeLaUrl() {
  if (!window.location.search) return;
  window.history.replaceState(null, '', window.location.pathname);
}

// ── Las tiles ───────────────────────────────────────────────────────────────

/** El nivel más grueso de la pirámide: con ese se arma la hoja completa. */
function nivelMasGrueso(hoja: Hoja): number {
  const niveles = (hoja.nivelesTiles || '0')
    .split(',').map(n => Number(n.trim())).filter(Number.isFinite);
  return niveles.length ? Math.max(...niveles) : 0;
}

/**
 * TODAS las tiles de la hoja al nivel más grueso. Para imprimir no sirve la
 * lista del visor: esa es el recorte de lo que se está viendo, y en papel va
 * la hoja entera.
 */
export function tilesDeLaHojaCompleta(hoja: Hoja): Tile[] {
  if (!hoja.baseUrlTiles || !hoja.anchoPx || !hoja.altoPx) return [];
  const nivel = nivelMasGrueso(hoja);
  const paso = (hoja.tileSize || 1024) * 2 ** nivel;
  const salida: Tile[] = [];
  for (let y = 0; y < Math.ceil(hoja.altoPx / paso); y++) {
    for (let x = 0; x < Math.ceil(hoja.anchoPx / paso); x++) {
      salida.push({
        x: x * paso, y: y * paso,
        // La última columna y la última fila se recortan: la hoja no es un
        // múltiplo exacto del paso y estirar esa tile corre todo el dibujo.
        w: Math.min(paso, hoja.anchoPx - x * paso),
        h: Math.min(paso, hoja.altoPx - y * paso),
        url: `${hoja.baseUrlTiles}_${nivel}_${x}_${y}.jpg${hoja.tokenTiles || ''}`,
      });
    }
  }
  return salida;
}

/**
 * ¿Se pueden LEER las tiles por código? Medido el 20-oct-2026 contra
 * `stlaminasarquiflow`: el preflight responde `CorsPreflightFailure`, así que
 * en producción hoy la respuesta es NO.
 *
 * Esto no es un detalle de implementación: sin CORS, meter una tile en un
 * canvas lo contamina y `toBlob` tira `SecurityError`. La instantánea PNG
 * depende de esto; IMPRIMIR no, porque ahí las imágenes las pinta el navegador
 * y nunca pasan por un canvas. Por eso el panel las trata distinto en vez de
 * ofrecer las dos y fallar en una.
 *
 * Se mide en vez de asumirse porque el arreglo es una línea de infraestructura
 * (`az storage cors add`) y el día que esté puesta esto tiene que encenderse
 * solo.
 */
export async function tilesCopiables(url: string | null): Promise<boolean> {
  if (!url) return false;
  try {
    const r = await fetch(url, { mode: 'cors' });
    return r.ok;
  } catch {
    return false;
  }
}

/**
 * Las tiles se bajan con `fetch` y se decodifican desde un Blob LOCAL, no con
 * `new Image()` + `crossOrigin`. Es a propósito: un Blob propio nunca
 * contamina el canvas, así que el único modo de falla posible es el fetch
 * bloqueado por CORS — que es explícito y se puede contar.
 */
async function imagenDeUrl(url: string): Promise<ImageBitmap> {
  const r = await fetch(url, { mode: 'cors' });
  if (!r.ok) throw new Error(`tile ${r.status}`);
  return createImageBitmap(await r.blob());
}

// ── La instantánea ──────────────────────────────────────────────────────────

/**
 * El SVG de marcas, serializado como imagen. Se clona el que YA está pintado
 * en vez de volver a recorrer el GeoJSON: el pintor vive en `CapaMarcas` y
 * duplicarlo acá sería la misma trampa que F-6 resolvió sacando `CapaTiles`
 * del visor.
 *
 * Dos arreglos obligados sobre el clon: el `<text>` se estiliza con clases de
 * Tailwind, que no viajan en la serialización (quedaría en la fuente del
 * sistema, sin negrita), y el SVG necesita ancho y alto propios o un
 * `<img>` no sabe a qué tamaño rasterizarlo.
 */
function svgComoImagen(svg: SVGSVGElement, hoja: Hoja): Promise<HTMLImageElement> {
  const clon = svg.cloneNode(true) as SVGSVGElement;
  clon.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clon.setAttribute('width', String(hoja.anchoPx));
  clon.setAttribute('height', String(hoja.altoPx));
  clon.querySelectorAll('text').forEach(t => {
    t.removeAttribute('class');
    t.setAttribute('font-family', 'sans-serif');
    t.setAttribute('font-weight', 'bold');
  });
  const texto = new XMLSerializer().serializeToString(clon);
  const url = URL.createObjectURL(new Blob([texto], { type: 'image/svg+xml' }));
  return new Promise((listo, falla) => {
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); listo(img); };
    img.onerror = () => { URL.revokeObjectURL(url); falla(new Error('svg')); };
    img.src = url;
  });
}

export interface PedidoInstantanea {
  hoja: Hoja;
  vista: Vista;
  tam: { w: number; h: number };
  /** Las tiles del VIEWPORT: la instantánea es lo que se está viendo. */
  visibles: Tile[];
  svgMarcas: SVGSVGElement | null;
}

/**
 * Compone en un canvas exactamente el encuadre que se ve. La transformación se
 * repite en el mismo orden que el CSS del visor —`translate`, `scale` y recién
 * después `rotate`, con origen arriba-izquierda— porque cualquier otro orden
 * mueve el dibujo respecto de lo que la persona tiene en pantalla.
 */
export async function componerInstantanea(p: PedidoInstantanea): Promise<Blob> {
  const { hoja, vista, tam, visibles, svgMarcas } = p;
  const lienzo = document.createElement('canvas');
  lienzo.width = Math.max(1, Math.round(tam.w));
  lienzo.height = Math.max(1, Math.round(tam.h));
  const ctx = lienzo.getContext('2d');
  if (!ctx) throw new Error('sin canvas 2d');

  // El papel. Sin esto el PNG sale con fondo transparente y pegado en un correo
  // de fondo oscuro el plano se vuelve ilegible.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, lienzo.width, lienzo.height);

  ctx.save();
  ctx.translate(vista.x, vista.y);
  ctx.scale(vista.escala, vista.escala);
  if (hoja.rotacionGrados) ctx.rotate((hoja.rotacionGrados * Math.PI) / 180);

  for (const t of visibles) {
    const bm = await imagenDeUrl(t.url);
    ctx.drawImage(bm, t.x, t.y, t.w, t.h);
    bm.close();
  }

  if (svgMarcas) {
    const img = await svgComoImagen(svgMarcas, hoja);
    ctx.drawImage(img, 0, 0, hoja.anchoPx, hoja.altoPx);
  }
  ctx.restore();

  return new Promise((listo, falla) => {
    // Acá es donde explota si una tile contaminó el canvas. Se traduce a un
    // mensaje con causa: «SecurityError» a secas no le dice a nadie que lo que
    // falta es una regla de CORS en el almacenamiento.
    try {
      lienzo.toBlob(b => (b ? listo(b) : falla(new Error('el canvas no dio PNG'))), 'image/png');
    } catch {
      falla(new Error('las tiles no permiten lectura por código (CORS)'));
    }
  });
}

export function descargar(blob: Blob, nombre: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  a.click();
  // Revocar en el mismo tick cancela la descarga en Firefox.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Nombre de archivo sin sorpresas: sin acentos, espacios ni barras. */
export function nombreDeArchivo(hoja: Hoja, lamina: string, ext: string): string {
  const limpio = `${lamina}-v${hoja.version}`
    // Escapes Unicode a propósito: el rango de diacríticos combinantes escrito
    // literal lo mastica cualquier herramienta que toque el archivo (footgun
    // PowerShell/UTF-8 documentado en CLAUDE.md).
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${limpio || 'lamina'}.${ext}`;
}

// ── Imprimir ────────────────────────────────────────────────────────────────

/**
 * Imprime la HOJA COMPLETA en una ventana aparte.
 *
 * Por qué una ventana y no `@media print` sobre la app: la hoja vive dentro de
 * un contenedor con `overflow-hidden` y un `transform` de pan/zoom, así que
 * imprimir el DOM vivo imprime el recorte de la pantalla. Y una ventana con
 * solo la hoja adentro no necesita esconder la mitad de la aplicación con
 * reglas `!important`.
 *
 * Las tiles van posicionadas en PORCENTAJE y el SVG al 100 % con su `viewBox`:
 * así la hoja se adapta al papel sin que haya que saber cuántos píxeles mide
 * una página, que es el dato que el navegador no da.
 *
 * NO pasa por un canvas, y por eso funciona aunque las tiles no permitan CORS:
 * es el navegador el que las pinta, como con cualquier `<img>`.
 */
export async function imprimirHoja(opts: {
  hoja: Hoja;
  titulo: string;
  svgMarcas: SVGSVGElement | null;
}): Promise<void> {
  const { hoja, titulo, svgMarcas } = opts;
  const tiles = tilesDeLaHojaCompleta(hoja);
  const pct = (v: number, total: number) => `${(v / total) * 100}%`;

  const imagenes = tiles.length
    ? tiles.map(t => `<img src="${t.url}" style="position:absolute;`
        + `left:${pct(t.x, hoja.anchoPx)};top:${pct(t.y, hoja.altoPx)};`
        + `width:${pct(t.w, hoja.anchoPx)};height:${pct(t.h, hoja.altoPx)}">`).join('')
    // Sin tiles queda la imagen entera, que es lo que siembra el ingestor local.
    : hoja.urlOriginal
      ? `<img src="${hoja.urlOriginal}" style="position:absolute;inset:0;width:100%;height:100%">`
      : '';

  let marcas = '';
  if (svgMarcas) {
    const clon = svgMarcas.cloneNode(true) as SVGSVGElement;
    clon.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clon.setAttribute('width', '100%');
    clon.setAttribute('height', '100%');
    clon.setAttribute('preserveAspectRatio', 'none');
    clon.removeAttribute('class');
    clon.querySelectorAll('text').forEach(t => {
      t.removeAttribute('class');
      t.setAttribute('font-family', 'sans-serif');
      t.setAttribute('font-weight', 'bold');
    });
    clon.setAttribute('style', 'position:absolute;inset:0');
    marcas = new XMLSerializer().serializeToString(clon);
  }

  // La hoja decide la orientación del papel: un plano acostado impreso en
  // vertical sale a un tercio del tamaño y con dos bandas blancas.
  const orientacion = hoja.anchoPx >= hoja.altoPx ? 'landscape' : 'portrait';

  const ventana = window.open('', '_blank');
  if (!ventana) throw new Error('el navegador bloqueó la ventana de impresión');
  ventana.document.write(`<!doctype html><html><head><meta charset="utf-8">
<title>${titulo.replace(/[<>&]/g, '')}</title>
<style>
  @page { size: ${orientacion}; margin: 8mm }
  html,body { margin:0; padding:0; background:#fff }
  .hoja { position:relative; width:100%; aspect-ratio:${hoja.anchoPx} / ${hoja.altoPx};
          background:#fff; overflow:hidden }
  img { display:block }
  @media print { .aviso { display:none } }
  .aviso { font:13px sans-serif; color:#5D636C; padding:8px }
</style></head><body>
<p class="aviso">Si no se abrió el diálogo de impresión, usá Ctrl/Cmd + P.</p>
<div class="hoja">${imagenes}${marcas}</div>
</body></html>`);
  ventana.document.close();

  // Imprimir antes de que las tiles carguen saca una hoja en blanco. Se espera
  // el `load` de la ventana, que ya incluye sus imágenes, con un tope: una tile
  // que nunca responde no puede dejar la impresión colgada para siempre.
  await new Promise<void>(listo => {
    let cerrado = false;
    const seguir = () => { if (!cerrado) { cerrado = true; listo(); } };
    ventana.addEventListener('load', seguir);
    setTimeout(seguir, 8000);
  });
  ventana.focus();
  ventana.print();
}
